import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { labelOf, colorOf } from "@/lib/statuses";
import { ArrowLeft, Plus, Trash2, ToggleLeft, ToggleRight, Save } from "lucide-react";

export default function AdminFixerDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [services, setServices] = useState([]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [savingPricing, setSavingPricing] = useState(false);

  // Local working copy of the offered-services list. Each entry:
  //   { service_id, labor_price: string, _new?: bool }
  // Compared against `data.pricing` on save to compute add/remove/update sets.
  const [draftPricing, setDraftPricing] = useState([]);
  const [addPick, setAddPick] = useState("");

  const load = useCallback(() => {
    api.get(`/admin/fixers/${id}/detail`).then((r) => {
      setData(r.data);
      setDraftPricing(
        (r.data.pricing || []).map((p) => ({
          service_id: p.service_id,
          labor_price: String(p.labor_price ?? ""),
        })),
      );
    }).catch(() => setData(null));
  }, [id]);

  useEffect(() => {
    load();
    api.get("/services").then((r) => setServices(r.data));
  }, [load]);

  const serviceById = useMemo(() => {
    const m = {};
    for (const s of services) m[s.id] = s;
    return m;
  }, [services]);

  const offeredIds = useMemo(
    () => new Set(draftPricing.map((p) => p.service_id)),
    [draftPricing],
  );
  const availableToAdd = services.filter((s) => !offeredIds.has(s.id));

  const updateDraftPrice = (sid, val) => {
    setDraftPricing((rows) =>
      rows.map((r) => (r.service_id === sid ? { ...r, labor_price: val } : r)),
    );
  };
  const removeDraft = (sid) => {
    setDraftPricing((rows) => rows.filter((r) => r.service_id !== sid));
  };
  const addDraft = () => {
    if (!addPick) return;
    setDraftPricing((rows) => [
      ...rows,
      { service_id: addPick, labor_price: "599", _new: true },
    ]);
    setAddPick("");
  };

  // True when local list differs from the loaded list.
  const isDirty = useMemo(() => {
    if (!data) return false;
    const original = (data.pricing || []).reduce((acc, p) => {
      acc[p.service_id] = String(p.labor_price ?? "");
      return acc;
    }, {});
    if (Object.keys(original).length !== draftPricing.length) return true;
    for (const r of draftPricing) {
      if (original[r.service_id] === undefined) return true;
      if (String(original[r.service_id]) !== String(r.labor_price)) return true;
    }
    return false;
  }, [data, draftPricing]);

  const saveServices = async () => {
    if (!isDirty || !data) return;
    setSavingPricing(true);
    try {
      const original = new Map(
        (data.pricing || []).map((p) => [p.service_id, String(p.labor_price)]),
      );
      const draftMap = new Map(
        draftPricing.map((r) => [r.service_id, String(r.labor_price)]),
      );
      // Removed = in original, not in draft
      const removed = [...original.keys()].filter((sid) => !draftMap.has(sid));
      // Upserts = in draft AND (not in original OR price changed)
      const upserts = [...draftMap.entries()].filter(
        ([sid, val]) => original.get(sid) !== val,
      );
      // Validate first — bail before mutating anything
      for (const [sid, val] of upserts) {
        const num = parseFloat(val);
        if (Number.isNaN(num) || num < 0) {
          toast.error(`Ugyldig pris for "${serviceById[sid]?.name || sid}"`);
          setSavingPricing(false);
          return;
        }
      }
      // Apply upserts + deletes
      await Promise.all([
        ...upserts.map(([sid, val]) =>
          api.patch(`/admin/fixers/${id}/pricing`, {
            service_id: sid,
            labor_price: parseFloat(val),
          }),
        ),
        ...removed.map((sid) =>
          api.delete(`/admin/fixers/${id}/pricing/${sid}`),
        ),
      ]);
      toast.success("Endringer lagret");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSavingPricing(false);
    }
  };

  const addNote = async (e) => {
    e.preventDefault();
    if (!note.trim()) return;
    setBusy(true);
    try {
      await api.post(`/admin/fixers/${id}/notes`, { text: note.trim() });
      setNote("");
      toast.success("Notat lagt til");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async () => {
    try {
      await api.patch(`/admin/fixers/${id}`, { active: !data.fixer.active });
      toast.success(data.fixer.active ? "Verksted deaktivert" : "Verksted aktivert");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  if (!data) {
    return (
      <div className="tk-container py-20 text-slate-500" data-testid="admin-fixer-loading">
        Laster …
      </div>
    );
  }

  const { fixer, owner, orders, invoices, notes } = data;
  const paidOrders = orders.filter((o) => o.payment_status === "paid");
  const totalEarnings = paidOrders.reduce((s, o) => s + Number(o.service_fee || 0), 0);
  const paidInvoiced = invoices
    .filter((i) => i.status === "paid")
    .reduce((s, i) => s + Number(i.amount || 0), 0);

  return (
    <div className="tk-container py-16 max-w-5xl" data-testid="admin-fixer-detail">
      <Link to="/admin" className="text-sm text-slate-500 hover:text-[#0B1C30] inline-flex items-center gap-1">
        <ArrowLeft size={14} /> Tilbake
      </Link>
      <div className="flex flex-wrap items-center gap-3 mt-6 mb-2">
        <p className="tk-overline">Verksted</p>
        <span className={`text-xs px-2 py-1 rounded-md ${fixer.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
          {fixer.active ? "Aktiv" : "Inaktiv"}
        </span>
      </div>
      <h1 className="text-3xl sm:text-4xl font-semibold mb-2">{fixer.display_name}</h1>
      <p className="text-sm text-slate-500 mb-2">
        {fixer.business_name} · {fixer.address}, {fixer.postal_code}
      </p>
      {owner && (
        <p className="text-sm text-slate-500 mb-8">
          Kontakt: {owner.name} · {owner.email} {owner.phone_number && `· ${owner.phone_number}`}
        </p>
      )}
      <button
        onClick={toggleActive}
        className="tk-btn-secondary !py-1.5 !px-3 text-xs mb-10"
        data-testid="toggle-active-btn"
      >
        {fixer.active ? (<><ToggleLeft size={14} /> Deaktiver</>) : (<><ToggleRight size={14} /> Aktiver</>)}
      </button>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        <div className="tk-card">
          <p className="tk-overline mb-2">Bestillinger</p>
          <p className="text-2xl font-semibold">{orders.length}</p>
        </div>
        <div className="tk-card">
          <p className="tk-overline mb-2">Betalte</p>
          <p className="text-2xl font-semibold">{paidOrders.length}</p>
        </div>
        <div className="tk-card">
          <p className="tk-overline mb-2">Tjent (arbeid)</p>
          <p className="text-2xl font-semibold">kr {totalEarnings.toFixed(0)}</p>
        </div>
        <div className="tk-card">
          <p className="tk-overline mb-2">Utbetalt</p>
          <p className="text-2xl font-semibold">kr {paidInvoiced.toFixed(0)}</p>
        </div>
      </div>

      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold">Tjenester verkstedet tilbyr</h2>
        {isDirty && (
          <span
            className="text-xs px-2 py-1 rounded bg-amber-50 text-amber-700"
            data-testid="pricing-dirty-indicator"
          >
            Ulagrede endringer
          </span>
        )}
      </div>

      <div className="tk-card mb-4" data-testid="fixer-pricing-table">
        {draftPricing.length === 0 ? (
          <p className="text-sm text-slate-500 py-2">
            Ingen tjenester registrert ennå. Legg til under.
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b border-[#E2E8F0]">
                <th className="py-2 pr-4 font-medium text-slate-500">Tjeneste</th>
                <th className="py-2 pr-4 font-medium text-slate-500">Pris (NOK)</th>
                <th className="py-2 pr-4"></th>
              </tr>
            </thead>
            <tbody>
              {draftPricing.map((row) => {
                const s = serviceById[row.service_id];
                return (
                  <tr
                    key={row.service_id}
                    className="border-b border-[#E2E8F0]/60"
                    data-testid={`pricing-row-${row.service_id}`}
                  >
                    <td className="py-2 pr-4">
                      {s?.name || row.service_id}
                      {row._new && (
                        <span className="ml-2 text-[10px] uppercase tracking-wider text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">
                          ny
                        </span>
                      )}
                    </td>
                    <td className="py-2 pr-4">
                      <input
                        type="number"
                        min="0"
                        value={row.labor_price}
                        onChange={(e) => updateDraftPrice(row.service_id, e.target.value)}
                        className="tk-input !py-1 !px-2 text-sm max-w-[120px]"
                        data-testid={`pricing-input-${row.service_id}`}
                      />
                    </td>
                    <td className="py-2 pr-4">
                      <button
                        type="button"
                        onClick={() => removeDraft(row.service_id)}
                        className="text-slate-400 hover:text-red-600 inline-flex items-center gap-1 text-xs"
                        data-testid={`pricing-remove-${row.service_id}`}
                        aria-label={`Fjern ${s?.name || row.service_id}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Add service picker */}
      {availableToAdd.length > 0 && (
        <div className="flex items-center gap-2 mb-4" data-testid="add-service-row">
          <select
            value={addPick}
            onChange={(e) => setAddPick(e.target.value)}
            className="tk-input !py-1.5 text-sm flex-1 max-w-sm"
            data-testid="add-service-picker"
          >
            <option value="">Velg tjeneste …</option>
            {availableToAdd.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={addDraft}
            disabled={!addPick}
            className="tk-btn-secondary !py-1.5 !px-3 text-xs"
            data-testid="add-service-btn"
          >
            <Plus size={14} /> Legg til tjeneste
          </button>
        </div>
      )}

      <button
        type="button"
        onClick={saveServices}
        disabled={!isDirty || savingPricing}
        className="tk-btn-primary mb-10"
        data-testid="save-pricing-btn"
      >
        <Save size={14} /> {savingPricing ? "Lagrer …" : "Lagre endringer"}
      </button>

      <h2 className="text-xl font-semibold mb-4">Siste bestillinger</h2>
      {orders.length === 0 ? (
        <p className="text-slate-500 mb-10">Ingen bestillinger.</p>
      ) : (
        <div className="overflow-x-auto mb-10">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b border-[#E2E8F0]">
                <th className="py-3 pr-4 font-medium text-slate-500">Ordre</th>
                <th className="py-3 pr-4 font-medium text-slate-500">Kunde</th>
                <th className="py-3 pr-4 font-medium text-slate-500">Reparasjon</th>
                <th className="py-3 pr-4 font-medium text-slate-500">Total</th>
                <th className="py-3 pr-4 font-medium text-slate-500">Status</th>
              </tr>
            </thead>
            <tbody>
              {orders.slice(0, 30).map((o) => (
                <tr key={o.id} className="border-b border-[#E2E8F0]/70 hover:bg-slate-50">
                  <td className="py-3 pr-4 font-mono text-xs">
                    <Link to={`/admin/bestilling/${o.id}`} className="text-slate-400 hover:text-[#0B1C30]">
                      #{o.id.slice(0, 8)} ↗
                    </Link>
                  </td>
                  <td className="py-3 pr-4">{o.customer_name}</td>
                  <td className="py-3 pr-4">{o.product_name}</td>
                  <td className="py-3 pr-4">kr {Number(o.total_price).toFixed(0)}</td>
                  <td className="py-3 pr-4"><span className={`text-xs px-2 py-1 rounded-md ${colorOf(o.status)}`}>{labelOf(o.status)}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h2 className="text-xl font-semibold mb-4">Interne notater</h2>
      <form onSubmit={addNote} className="tk-card mb-6 flex gap-2 items-start" data-testid="add-fixer-note-form">
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Skriv et notat (kun synlig for admin)"
          className="tk-input flex-1 min-h-[60px]"
          data-testid="fixer-note-input"
        />
        <button type="submit" disabled={busy} className="tk-btn-primary" data-testid="add-fixer-note-btn">
          <Plus size={14} /> Legg til
        </button>
      </form>
      {notes.length === 0 ? (
        <p className="text-slate-500">Ingen notater enda.</p>
      ) : (
        <ul className="space-y-2">
          {notes.map((n) => (
            <li key={n.id} className="tk-card">
              <p className="text-sm">{n.text}</p>
              <p className="text-xs text-slate-400 mt-2">
                {n.author_email} · {new Date(n.created_at).toLocaleString("nb-NO")}
              </p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
